import { useCallback, useState, type PointerEvent } from 'react';
import type { ChartModel } from './lib/chartModel';
import type { LinearScale } from './lib/layout';
import { snapTime } from './lib/hover';

/** The hovered instant: the snapped reading time, the pointer's own time, the ms a few pixels span, and the pointer's viewport position. */
export interface Hover {
  at: number;
  pointer: number;
  tolerance: number;
  clientX: number;
  clientY: number;
}

/** The crosshair state shared by every chart, and the pointer handlers each chart's plot uses. */
export interface Crosshair {
  hover: Hover | null;
  move: (event: PointerEvent<SVGSVGElement>) => void;
  leave: () => void;
}

const DRAGGING = '[data-dragging]';
const TOLERANCE_PX = 4;

/**
 * Tracks the hovered instant across the stack, snapped to the nearest reading on any chart, or the pointer's own
 * time when no device is visible. Cleared outside the plot area, while a handle is dragged, and on leaving the plot.
 */
export const useCrosshair = (
  models: readonly ChartModel[],
  xScale: LinearScale
): Crosshair => {
  const [hover, setHover] = useState<Hover | null>(null);

  const move = useCallback(
    (event: PointerEvent<SVGSVGElement>) => {
      const x =
        event.clientX - event.currentTarget.getBoundingClientRect().left;
      const [left, right] = xScale.range();
      const dragging =
        event.target instanceof Element && event.target.closest(DRAGGING);
      if (x < left || x > right || dragging) {
        setHover(null);
        return;
      }
      const pointer = xScale.invert(x);
      setHover({
        at: snapTime(models, pointer) ?? pointer,
        pointer,
        tolerance: Math.abs(xScale.invert(x + TOLERANCE_PX) - pointer),
        clientX: event.clientX,
        clientY: event.clientY,
      });
    },
    [models, xScale]
  );

  const leave = useCallback(() => setHover(null), []);

  return { hover, move, leave };
};
